import React from 'react';
import PropTypes from 'prop-types';

import { withStyles } from '@material-ui/core/styles';

import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';

import * as d3 from 'd3';

import StatisticsOverview from './statisticsOverview'

const styles = theme => ({
  card: {
    display: 'flex column',
    margin: 0,
    borderColor: 'rgba(255,255,255,0)!important',
    boxShadow: '0px 0px 0px 0px rgba(255,255,255,0)!important',
    backgroundColor: '#f3f3f3',
  },
  cardHeader: {
    backgroundColor: '#069999',
    color: '#fff',
    padding: '10px',
    fontFamily: 'Nunito Sans SemiBold!important',
    textAlign: 'left',
    fontSize: '16pt',
    borderRadius: '10px',
    minHeight: '80px',
    maxHeight: '80px',
  },
  cardContent: {
    padding: '0!important',
    marginTop: '20px;',
    height: '390px',
  },
});

function StatisticsOverviewCard(props) {
  const { classes } = props;

  let locale
  switch (props.lang) {
    case "de":
      locale = d3.timeFormatDefaultLocale({
        "decimal": ",",
        "thousands": ".",
        "grouping": [3],
        "currency": ["€", ""],
        "dateTime": "%a %b %e %X %Y",
        "date": "%d-%m-%Y",
        "time": "%H:%M:%S",
        "periods": ["AM", "PM"],
        "days": ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"],
        "shortDays": ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
        "months": ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
        "shortMonths": ["Jan", "", "Mar", "", "Mai", "", "Jul", "", "Sep", "", "Nov", ""]
      })
      break;
    case "en":
      locale = d3.timeFormatDefaultLocale({
        "decimal": ",",
        "thousands": ".",
        "grouping": [3],
        "currency": ["€", ""],
        "dateTime": "%a %b %e %X %Y",
        "date": "%d-%m-%Y",
        "time": "%H:%M:%S",
        "periods": ["AM", "PM"],
        "days": ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "shortDays": ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
        "months": ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
        "shortMonths": ["Jan", "", "Mar", "", "May", "", "Jul", "", "Sep", "", "Nov", ""]
      })
      break;
    case "fr":
    default:
      locale = d3.timeFormatDefaultLocale({
        "decimal": ",",
        "thousands": ".",
        "grouping": [3],
        "currency": ["€", ""],
        "dateTime": "%a %b %e %X %Y",
        "date": "%d-%m-%Y",
        "time": "%H:%M:%S",
        "periods": ["AM", "PM"],
        "days": ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"],
        "shortDays": ["Di", "Lu", "Ma", "Mi", "Je", "Ve", "Sa"],
        "months": ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"],
        "shortMonths": ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"]
      })
  }

  let energyType
  switch (props.lang) {
    case "de":
      energyType = (
        props.currentTech.solar ? (
          props.currentTech.wind ? (
            props.currentTech.windOffshore ?
              "Solar-, Onshore- und Offshore-Wind"
              : "Solar- und Onshore-Wind"
          )
            : (
              props.currentTech.windOffshore ?
                "Solar- und Offshore-Wind"
                : "Solar"
            )
        )
          : (
            props.currentTech.wind ? (
              props.currentTech.windOffshore ?
                "Onshore- und Offshore-Wind" :
                "Onshore-Wind"
            )
              : "Offshore-Wind"
          )
      )
      break
    case "en":
      energyType = (
        props.currentTech.solar ? (
          props.currentTech.wind ? (
            props.currentTech.windOffshore ?
              "Solar,onshore and offshore wind"
              : "Solar and onshore wind"
          )
            : (
              props.currentTech.windOffshore ?
                "Solar and offshore wind"
                : "Solar"
            )
        )
          : (
            props.currentTech.wind ? (
              props.currentTech.windOffshore ?
                "Onshore and offshore wind" :
                "Onshore wind"
            )
              : "Offshore wind"
          )
      )
      break
    case "fr":
    default:
      energyType = (
        props.currentTech.solar ? (
          props.currentTech.wind ? (
            props.currentTech.windOffshore ?
              "solaire, éolienne onshore et offshore"
              : "solaire et éolienne onshore"
          )
            : (
              props.currentTech.windOffshore ?
                "solaire et éolienne offshore"
                : "solaire"
            )
        )
          : (
            props.currentTech.wind ? (
              props.currentTech.windOffshore ?
                "éolienne onshore et offshore" :
                "éolienne onshore"
            )
              : "éolienne offshore"
          )
      )
  }

  let cardHeaderTitle
  let dateInfo
  if (props.currentMode === 'realtime') {
    let formattedDay
    switch (props.lang) {
      case "de":
        formattedDay = d3.timeFormat('%e. %B %Y')(props.selectedDate)
        dateInfo = "am " + formattedDay
        break
      case "en":
        formattedDay = d3.timeFormat('%e %B %Y')(props.selectedDate)
        dateInfo = formattedDay
        break
      case "fr":
      default:
        formattedDay = d3.timeFormat('%e %B %Y')(props.selectedDate)
        dateInfo = " le " + formattedDay
    }
  }
  else {
    let formattedYear = props.yearSelected
    switch (props.lang) {
      case "de":
        dateInfo = formattedYear
        break
      case "en":
        dateInfo = formattedYear
        break
      case "fr":
      default:
        dateInfo = " en " + formattedYear
    }
  }
  switch (props.lang) {
    case "de":
      cardHeaderTitle = energyType + "energie in der Wallonie " + dateInfo
      break
    case "en":
      cardHeaderTitle = energyType + " energy in Wallonia, " + dateInfo
      break
    case "fr":
    default:
      cardHeaderTitle = "Énergie " + energyType + " en Wallonie " + dateInfo
  }

  return (
    <Card className={classes.card}>
      <CardHeader
        className={classes.cardHeader}
        disableTypography
        title={cardHeaderTitle}
      />

      <CardContent className={classes.cardContent}>
        <StatisticsOverview
          statisticsData={props.statisticsData}
          currentTech={props.currentTech}
          currentMode={props.currentMode}
          yearSelected={props.yearSelected}
          dataLoaded={props.dataLoaded}
          lang={props.lang}
        />
      </CardContent>
    </Card>
  );
}

StatisticsOverviewCard.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(StatisticsOverviewCard);
